
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/AuthContext";
import { tasksService, reportsService } from "@/services";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Briefcase,
  Search,
  MapPin,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { toast } from "sonner";

const categoryIcons = {
  pothole: "🕳️",
  blocked_drain: "🚰",
  waste_collection: "🗑️",
  street_lighting: "💡",
  flooding: "🌊",
  vandalism: "⚠️",
  illegal_dumping: "🚮",
  broken_infrastructure: "🔧",
  water_leak: "💧",
  other: "📍"
};

const categoryPayments = {
  pothole: 500,
  blocked_drain: 300,
  waste_collection: 200,
  street_lighting: 400,
  flooding: 600,
  vandalism: 450,
  illegal_dumping: 250,
  broken_infrastructure: 550,
  water_leak: 400,
  other: 300
};

export default function ContractorJobs() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const { user } = useAuth();

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ['availableJobs'],
    queryFn: () => reportsService.filter({ 
      status: 'acknowledged' 
    }, '-upvote_count'),
    initialData: [],
  });

  const { data: allTasks = [] } = useQuery({
    queryKey: ['allTasks'],
    queryFn: () => tasksService.list(),
    initialData: [],
  });

  const claimJobMutation = useMutation({
    mutationFn: async ({ reportId, category }) => {
      // Create task
      const task = await tasksService.create({
        report_id: reportId,
        contractor_email: user.email,
        payment_amount: categoryPayments[category] || 300,
        status: 'claimed',
        claimed_date: new Date().toISOString(),
      });

      // Update report status
      await reportsService.update(reportId, {
        status: 'in_progress',
        assigned_to: user.email,
      });

      return task;
    },
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: ['availableJobs'] });
      queryClient.invalidateQueries({ queryKey: ['allTasks'] });
      toast.success(`🎯 Job claimed! Earn R${task.payment_amount} by completing this task.`, {
        duration: 4000,
      });
    },
    onError: (error) => {
      toast.error("Failed to claim job. It may have been claimed by another contractor.");
      console.error("Error claiming job:", error);
    },
  });

  // Filter out reports that already have tasks
  const claimedReportIds = allTasks.map(task => task.report_id);
  const availableReports = reports.filter(report => !claimedReportIds.includes(report.id));

  const filteredReports = availableReports.filter(report => {
    const categoryMatch = selectedCategory === "all" || report.category === selectedCategory;
    const searchMatch = !searchTerm || 
      report.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.neighborhood?.toLowerCase().includes(searchTerm.toLowerCase());
    return categoryMatch && searchMatch;
  });

  // Redirect non-contractors to MapView (role refresh will fix this within 30s)
  if (!user || user.user_role !== 'contractor') {
    navigate(createPageUrl("MapView"));
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4 md:p-8 overflow-y-auto">
      <div className="max-w-7xl mx-auto pb-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Briefcase className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Available Jobs</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-300">
            Claim jobs and earn by fixing community infrastructure
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 md:p-5 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-gray-800 border-emerald-200 dark:border-emerald-800 shadow-md">
            <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-1">Available Jobs</p>
            <p className="text-2xl md:text-3xl font-bold text-emerald-700 dark:text-emerald-300">{availableReports.length}</p>
          </Card>
          <Card className="p-4 md:p-5 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 border-blue-200 dark:border-blue-800 shadow-md">
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">Total Earnings</p>
            <p className="text-2xl md:text-3xl font-bold text-blue-700 dark:text-blue-300">
              R{availableReports.reduce((sum, r) => sum + (categoryPayments[r.category] || 300), 0).toLocaleString()}
            </p>
          </Card>
          <Card className="p-4 md:p-5 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-gray-800 border-purple-200 dark:border-purple-800 shadow-md">
            <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">High Priority</p>
            <p className="text-2xl md:text-3xl font-bold text-purple-700 dark:text-purple-300">
              {availableReports.filter(r => (r.upvote_count || 0) >= 3).length}
            </p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-md">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="pothole">Pothole</SelectItem>
                <SelectItem value="blocked_drain">Blocked Drain</SelectItem>
                <SelectItem value="waste_collection">Waste</SelectItem>
                <SelectItem value="street_lighting">Lighting</SelectItem>
                <SelectItem value="flooding">Flooding</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Jobs List */}
        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="p-6 animate-pulse bg-white dark:bg-gray-800">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
              </Card>
            ))}
          </div>
        ) : filteredReports.length === 0 ? (
          <Card className="p-12 text-center bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-md">
            <CheckCircle className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Available Jobs</h3>
            <p className="text-gray-600 dark:text-gray-300">
              {searchTerm || selectedCategory !== 'all'
                ? 'Try adjusting your filters'
                : 'Check back later for new opportunities'}
            </p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredReports.map((report) => {
              const payment = categoryPayments[report.category] || 300;
              const isHighPriority = (report.upvote_count || 0) >= 3;

              return (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -6, scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="p-5 md:p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-emerald-100 dark:border-emerald-900 hover:shadow-2xl transition-all">
                    {isHighPriority && (
                      <Badge className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 mb-3">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        High Priority
                      </Badge>
                    )}
                    
                    <div className="flex items-start gap-3 mb-4">
                      <span className="text-3xl flex-shrink-0">{categoryIcons[report.category]}</span>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                          {report.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
                          {report.description}
                        </p>
                      </div>
                    </div>

                    {/* Payment */}
                    <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Payment</span>
                        <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <DollarSign className="w-5 h-5" />
                          R{payment}
                        </span>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{report.neighborhood || report.address}</span>
                    </div>

                    {/* Engagement */}
                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-4 pb-4 border-b dark:border-gray-700">
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        <span>{report.upvote_count || 0} upvotes</span>
                      </div>
                      <span>•</span>
                      <span>Posted {format(new Date(report.created_date), 'MMM d')}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(createPageUrl(`ReportDetails?id=${report.id}`));
                        }}
                        className="flex-1"
                      >
                        Details
                      </Button>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          claimJobMutation.mutate({ 
                            reportId: report.id, 
                            category: report.category 
                          });
                        }}
                        disabled={claimJobMutation.isPending}
                        className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-md"
                      >
                        Claim Job
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
